import { AlertTriangle, CircleDollarSign, ExternalLink, LoaderCircle, RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getWorkbenchPaymentSummary } from "../../lib/arRecoveryPaymentsApi";

type Summary = Awaited<ReturnType<typeof getWorkbenchPaymentSummary>>["summary"];

export function ARRecoveryWorkbenchPaymentSummary({ accountId }: { accountId: string }) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      setSummary((await getWorkbenchPaymentSummary(accountId)).summary);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Payment summary could not be loaded.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setSummary(null);
    void load();
    // Payment summary is intentionally lazy and scoped to the selected account.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  return (
    <section className="ar-workspace-surface min-w-0 overflow-hidden rounded-[1.5rem] border p-4" aria-label="Account payment summary">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl border border-[#BFEAFF] bg-white text-[#14B8A6]">
            <CircleDollarSign className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[.16em] text-[#14B8A6]">Payment Summary</p>
            <h3 className="mt-1 text-lg font-black text-[#0A2540]">Client-reported payment activity</h3>
            <p className="mt-1 text-xs font-semibold leading-5 text-[#64748B]">Loaded independently from the certified account workspace.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="focus-ring inline-flex min-h-9 items-center gap-2 rounded-full border border-[#BFEAFF] bg-white px-3 text-[11px] font-black uppercase tracking-[.1em] text-[#0A2540]" type="button" onClick={() => void load()} disabled={loading}>
            {loading ? <LoaderCircle className="size-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="size-4" aria-hidden="true" />} Refresh
          </button>
          <Link className="focus-ring inline-flex min-h-9 items-center gap-2 rounded-full border border-[#14B8A6] bg-[#14B8A6] px-3 text-[11px] font-black uppercase tracking-[.1em] text-white" to={`/admin/ar-recovery/payments?accountId=${encodeURIComponent(accountId)}&return=account-recovery`}>
            Open Payment Center <ExternalLink className="size-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
      {error ? <div className="mt-3 flex items-start gap-2 rounded-xl border border-[#FED7AA] bg-[#FFF7ED] px-3 py-2 text-xs font-bold text-[#9A3412]"><AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />{error}</div> : null}
      {loading && !summary ? <div className="mt-4 flex min-h-24 items-center justify-center gap-2 text-sm font-bold text-[#475569]"><LoaderCircle className="size-5 animate-spin text-[#14B8A6]" aria-hidden="true" />Loading payment summary</div> : null}
      {summary ? (
        <>
          <dl className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryMetric label="Reported" value={String(summary.totalReportedPayments)} />
            <SummaryMetric label="Approved" value={String(summary.totalApprovedPayments)} />
            <SummaryMetric label="Unapplied" value={money(summary.currentUnappliedAmountCents)} />
            <SummaryMetric label="Pending Verification" value={money(summary.currentPendingVerificationAmountCents)} />
            <SummaryMetric label="Open Exceptions" value={String(summary.openPaymentExceptions)} />
            <SummaryMetric label="Possible Duplicates" value={String(summary.possibleDuplicates)} />
            <SummaryMetric label="Most Recent" value={summary.mostRecentPayment ? money(summary.mostRecentPayment.amountCents) : "None"} />
            <SummaryMetric label="Promise Outcome" value={summary.relatedPromiseOutcome?.confirmedOutcome || summary.relatedPromiseOutcome?.recommendedOutcome || "Not linked"} />
          </dl>
          {summary.recentActivity.length ? (
            <div className="mt-4 grid gap-2 md:grid-cols-2">
              {summary.recentActivity.slice(0, 4).map((event) => (
                <div key={event.publicationId} className="rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2">
                  <p className="text-xs font-black text-[#0A2540]">{event.eventType.replaceAll("_", " ")}</p>
                  <p className="mt-1 text-[11px] font-semibold text-[#64748B]">{event.target.replaceAll("_", " ")} · {dateTime(event.occurredAt)}</p>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function SummaryMetric({ label, value }: { label: string; value: string }) {
  return <div className="min-w-0 rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2"><dt className="text-[10px] font-black uppercase tracking-[.12em] text-[#14B8A6]">{label}</dt><dd className="mt-1 break-words text-sm font-black text-[#0A2540]">{value}</dd></div>;
}

function money(cents: number) { return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100); }
function dateTime(value: string) { const parsed = new Date(value); return Number.isNaN(parsed.getTime()) ? value : new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(parsed); }
